import { type ReactNode, createContext, useContext, useRef } from 'react';
import {
  useRadioGroup,
  VisuallyHidden,
  useRadio,
  useFocusRing,
} from 'react-aria';
import { useRadioGroupState, type RadioGroupState } from 'react-stately';
import { Heading } from '@hungryroot/ramen';
import styles from './RadioGroup.module.css';

interface Props<D extends string> {
  label: string;
  children: ReactNode;
  value: D;
  onChange: (value: D) => void;
}

const RadioContext = createContext<RadioGroupState>({} as RadioGroupState);

/**
 * @deprecated Use `RadioGroup` and `RadioOption` from `@hungryroot/ramen` instead.
 */
export function RadioGroup<D extends string>({
  onChange,
  label,
  children,
  value,
}: Props<D>) {
  const radioGroupState = useRadioGroupState({
    onChange(next) {
      onChange(next as D);
    },
    value,
  });
  const { radioGroupProps } = useRadioGroup(
    {
      label,
    },
    radioGroupState
  );
  return (
    <div {...radioGroupProps} className={styles.group}>
      <RadioContext.Provider value={radioGroupState}>
        {children}
      </RadioContext.Provider>
    </div>
  );
}

interface RadioProps<D extends string> {
  label: string;
  illustration?: ReactNode;
  value: D;
  testid?: string;
}

function getClassName({
  isSelected,
  isFocus,
}: {
  isSelected: boolean;
  isFocus: boolean;
}) {
  if (isSelected === true) {
    return [styles.active];
  }

  if (isFocus === true) {
    return [styles.radio, styles.focus];
  }

  return [styles.radio];
}

export function Radio<D extends string>({
  illustration,
  label,
  value,
  testid,
}: RadioProps<D>) {
  const state = useContext(RadioContext);
  const ref = useRef<HTMLInputElement | null>(null);
  const { isFocusVisible, focusProps } = useFocusRing();
  const { inputProps, isSelected } = useRadio(
    {
      children: label,
      value,
    },
    state,
    ref
  );

  return (
    <label
      className={getClassName({ isSelected, isFocus: isFocusVisible }).join(
        ' '
      )}
    >
      <VisuallyHidden>
        <input data-testid={testid} {...inputProps} {...focusProps} ref={ref} />
      </VisuallyHidden>
      <Heading className={styles['radio-label']} variant="H6">
        {label}
      </Heading>
      {illustration && (
        <div className={styles.illustration}>{illustration}</div>
      )}
    </label>
  );
}
